import React, { useState, useEffect } from "react";
import { Row, Col, Form, OverlayTrigger, Tooltip, Image } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import InputBox from "../common/InputBox";
import BuildingIcon from "../../assets/icons/Building.svg";
import DocSearchIcon from "../../assets/icons/DocSearch.svg";
import SettingNoteIcon from "../../assets/icons/SettingNote.svg";
import Alert from "../common/Alert";
import Loader from "../../assets/loader.gif";
import { getOBAmendmentData, addOBAmendmentData, updateOrderBookingData } from "../../services/orderBooking-service";
import { getTimelineData, getFileNames, downloadFile, deleteFile } from "../../services/timeline-service";
import { validINRCurrency } from "../../helpers/formatter";
import refreshTimeIcon from "../../assets/icons/RefreshTime.svg";
import RemarkHistoryModal from "../common/RemarkHistoryModal";
import ApprovalCommentsModal from "../common/ApprovalCommentsModal";
import { formatDate } from "../../helpers/date-formatter";
import { getSharepointList } from "../../services/enquiryMaster-service";
import { getSalesUser } from "../../services/discount-services";

export default function AmendmentHistory({ formValues, enquiryData, onBack, isobData }) {
    const [loading, setLoading] = useState(false);
    const [originalData, setOriginalData] = useState([]);
    const [amendmentFileNames, setAmendmentFileNames] = useState({});
    const [amendmentForm, setAmendmentForm] = useState({});
    const [expandedIndex, setExpandedIndex] = useState(null);
    const [showApprovalComments, setShowApprovalComments] = useState(false);
    const [activeApprovalComments, setActiveApprovalComments] = useState("");
    const [timelineDates, setTimelineDates] = useState({});
    const [errors, setErrors] = useState({});
    const [mandatoryFields, setMandatoryFields] = useState({});
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
    const [pendingWith, setPendingWith] = useState({ id: null, name: "" });
    const [salesUserMap, setSalesUserMap] = useState({});
    const NEW_RECORD_KEY = "new";

    const showNewRecord = !loading && (() => {
        if (originalData.length === 0) return true;
        const latest = originalData[0];
        const status = latest?.AmendStatus?.toLowerCase();
        const isSettled = status === "approved" || status === "rejected";
        return isSettled;
    })();

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 767);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetchAmendmentData();
        fetchTimelineDates();
        fetchPendingWith();
    }, []);

    const fetchAmendmentData = async () => {
        if (!formValues?.ID) return;
        setLoading(true);
        try {
            const res = await getOBAmendmentData({ order: formValues.ID });
            if (res?.data && res.data.length > 0) {
                setOriginalData(res.data);
                const fileMap = {};
                await Promise.all(res.data.map(async (record) => {
                    if (record.ID) {
                        try {
                            const filenames = await getFileNames(Number(record.ID), 'orderAmendment');
                            fileMap[record.ID] = filenames;
                        } catch {
                            fileMap[record.ID] = [];
                        }
                    }
                }));
                setAmendmentFileNames(fileMap);
            }
        } catch (err) {
            console.error("Error fetching amendment:", err);
        }
        setLoading(false);
    };

    const fetchPendingWith = async () => {
        try {
            const obEnquiryId = formValues?.Title;
            const [enquiryList, salesUsers] = await Promise.all([getSharepointList(), getSalesUser()]);
            const salesUsersList = salesUsers?.data || salesUsers || [];
            const map = {};
            salesUsersList.forEach((u) => { map[String(u.SalespersonId)] = u.field_1; });
            setSalesUserMap(map);
            const obRecord = (enquiryList?.data || enquiryList || []).find(
                (r) => Number(r.ID) === Number(obEnquiryId)
            );
            const assignedToId = obRecord?.AssignedToId;
            if (!assignedToId) return;
            const salesUser = salesUsersList.find((u) => Number(u.SalespersonId) === Number(assignedToId));
            setPendingWith({ id: assignedToId, name: salesUser?.field_1 || "" });
        } catch (err) {
            console.error("Error fetching pending with:", err);
        }
    };

    console.log("salesUserMap", salesUserMap);

    const fetchTimelineDates = async () => {
        try {
            const response = await getTimelineData();
            const enquiryId = enquiryData?.ID || formValues?.Title;
            const record = (response.data || []).find(
                (t) => Number(t.Title) === Number(enquiryId)
            );
            if (record) {
                setTimelineDates({
                    DesignStartDate: record.field_18,
                    DesignFinishDate: record.field_19,
                    SupplyStartDate: record.field_21,
                    SupplyFinishDate: record.field_22,
                    InstallationStartDate: record.field_24,
                    InstallationFinishDate: record.field_25,
                });
            }
        } catch (err) {
            console.error("Error fetching timeline dates:", err);
        }
    };

    const TODAY = new Date().toISOString().split("T")[0];

    const getEditedRecord = (index) => {
        if (index === NEW_RECORD_KEY) return { RequestDate: TODAY, ...(amendmentForm[NEW_RECORD_KEY] || {}) };
        return amendmentForm[index] || originalData[index];
    };

    const isRecordDirty = (index) => {
        if (index !== NEW_RECORD_KEY || !amendmentForm[NEW_RECORD_KEY]) return false;
        const rec = amendmentForm[NEW_RECORD_KEY] || {};
        return Object.values(rec).some(v => v !== "" && v !== undefined);
    };

    const handleInputChange = (e, index) => {
        let { name, value } = e.target;
        setAmendmentForm((prev) => ({
            ...prev,
            [index]: {
                ...(prev[index] || originalData[index]),
                [name]: value,
            },
        }));
    };

    const handleReset = (index) => {
        setAmendmentForm((prev) => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
        });
        setErrors((prev) => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
        });
        setMandatoryFields((prev) => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
        });
    };

    const buildPayload = (record) => ({
        RequestDate: new Date().toISOString().split("T")[0],
        DwgNo: record.DrawingNo ?? null,
        CustomerRequestDetails: record.CustomerRequestDetails ?? null,
        TechnicalChanges: record.TechnicalChanges ?? null,
        ValueChange: record.ValueChange ?? null,
        WeightChange: record.WeightChange ?? null,
        AmendStatus: "Pending for Approval",
        AdditionalDaysPOE: record.AdditionalDaysPOE ?? null,
        AdditionalDaysProjects: record.AdditionalDaysMFG ?? null,
        AdditionalCost: record.BodyCarryImpact ?? null,
        ApprovalComments: record.ApprovalComments ?? null,
        OBID: `${formValues?.ID}`,
        Title: formValues?.ProjectReferenceNo ?? null,
        PendingWith: pendingWith.id ?? null
    });

    const handleSubmit = async () => {
        const recordToSubmit = getEditedRecord(NEW_RECORD_KEY);
        const payload = buildPayload(recordToSubmit);
        try {
            await addOBAmendmentData(payload);
            await updateOrderBookingData({
                ID: formValues?.ID,
                CCR_Status: "Salesperson",
                AmendmentPending: payload.AmendStatus
            });
            Alert("success", "Order Amendments requested");
            setOriginalData((prev) => [{ ...recordToSubmit, AmendStatus: "Pending for Approval", RequestDate: payload.RequestDate }, ...prev]);
            setAmendmentForm((prev) => { const updated = { ...prev }; delete updated[NEW_RECORD_KEY]; return updated; });
        } catch (err) {
            console.error("Error submitting amendment:", err);
            Alert("error", "Failed to submit amendment");
        }
    };

    const handleToggleExpand = (index) => {
        setExpandedIndex((prev) => (prev === index ? null : index));
    };

    const getStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case "approved": return "amendment-status-approved";
            case "rejected": return "amendment-status-rejected";
            case "pending": return "amendment-status-pending";
            case "draft": return "amendment-status-draft";
            default: return "amendment-status-pending";
        }
    };

    const getCommonInputProps = (index) => ({
        setError: (updater) => {
            setErrors((prev) => ({
                ...prev,
                [index]: typeof updater === "function" ? updater(prev[index] || {}) : updater,
            }));
        },
        mandatoryFields: mandatoryFields[index] || [],
        setMandatoryFields: (updater) => {
            setMandatoryFields((prev) => ({
                ...prev,
                [index]: typeof updater === "function" ? updater(prev[index] || []) : updater,
            }));
        },
        onChange: (e) => handleInputChange(e, index),
    });

    const isSubmitDisabled = () => {
        const hasMandatoryErrors = (mandatoryFields[NEW_RECORD_KEY] || []).length > 0;
        return !isRecordDirty(NEW_RECORD_KEY) || hasMandatoryErrors;
    };

    return (
        <>
            {loading && (
                <div className="loader-box">
                    <img src={Loader} alt="Loading" className="newloader" />
                </div>
            )}
            <div className="add-isob-booking-container">
                <div className="add-isob-header">
                    <h4 className="ob-details-title">Order booking details</h4>
                    <div className="back-btn" onClick={onBack}>
                        <span className="back-btn-arrow">&lt;</span>
                        &nbsp;
                        <span className="back-btn-label">Back to Order Booking</span>
                    </div>
                </div>

                <div className="scrollForm amendment">
                    {/* Details Card */}
                    <div className="ob-details-card">
                        <div className="ob-details-item">
                            <div className="ob-details-icon">
                                <img src={BuildingIcon} alt="Customer" />
                            </div>
                            <div className="ob-details-text">
                                <span className="ob-details-label">Customer</span>
                                <span className="ob-details-value">
                                    {enquiryData?.AccountName || formValues?.CustomerName || "--"}
                                </span>
                            </div>
                        </div>
                        <div className="ob-details-divider"></div>
                        <div className="ob-details-item">
                            <div className="ob-details-icon">
                                <img src={DocSearchIcon} alt="Enquiry" />
                            </div>
                            <div className="ob-details-text">
                                <span className="ob-details-label">Enquiry No.</span>
                                <span className="ob-details-value">
                                    {enquiryData?.ID || formValues?.Title || "--"}
                                </span>
                            </div>
                        </div>
                        <div className="ob-details-divider"></div>
                        <div className="ob-details-item">
                            <div className="ob-details-icon">
                                <img src={SettingNoteIcon} alt="Project" />
                            </div>
                            <div className="ob-details-text">
                                <span className="ob-details-label">Project Reference No.</span>
                                <span className="ob-details-value">
                                    {enquiryData?.ProjectReferenceNo || formValues?.ProjectReferenceNo || "--"}
                                </span>
                            </div>
                        </div>
                        <div className="ob-details-item ob-details-order-value">
                            <div className="ob-details-text">
                                <span className="ob-details-label">Order Value (INR)</span>
                                <span className="ob-details-value ob-value-inr">
                                    ₹ {isobData?.tab3?.pgCommercial?.FinalBasicPrice
                                        ? validINRCurrency(isobData?.tab3?.pgCommercial?.FinalBasicPrice)
                                        : formValues?.OrderValueLakhs ? validINRCurrency(formValues.OrderValueLakhs) : ""}
                                </span>
                                <span className="ob-value-lacs">
                                    {isobData?.tab3?.pgCommercial?.FinalBasicPrice
                                        ? `${Math.round(Number(isobData?.tab3?.pgCommercial?.FinalBasicPrice) / 100000)} Lacs`
                                        : formValues?.OrderValueLakhs ? `${Math.round(Number(formValues?.OrderValueLakhs) / 100000)} Lacs` : ""}
                                </span>

                            </div>
                        </div>
                    </div>

                    {/* Amendment History Section */}
                    <h6 className="section-subheading ob-section-heading">Amendment History</h6>

                    <div className="amendment-timeline">
                        {showNewRecord && (() => {
                            const index = NEW_RECORD_KEY;
                            const currentRecord = getEditedRecord(index);
                            const isExpanded = expandedIndex === index;
                            const inputProps = getCommonInputProps(index);
                            const recordErrors = errors[index] || {};
                            const hasMore = originalData.length > 0;
                            return (
                              <div
                                className="amendment-timeline-item"
                                key="new-record"
                              >
                                <div className="amendment-timeline-dot"></div>
                                {hasMore && (
                                  <div className="amendment-timeline-line"></div>
                                )}
                                <div className="amendment-card">
                                  <div
                                    className="amendment-card-header"
                                    onClick={() => handleToggleExpand(index)}
                                  >
                                    <div className="amendment-header-left">
                                      <span className="amendment-status-badge amendment-status-draft">
                                        Draft
                                      </span>
                                      <span className="amendment-date">
                                        Requested date:{" "}
                                        {new Date().toLocaleDateString("en-GB")}
                                      </span>
                                    </div>
                                    <span
                                      className={`amendment-chevron ${isExpanded ? "expanded" : ""}`}
                                    >
                                      &#9662;
                                    </span>
                                  </div>
                                  {isExpanded && (
                                    <div className="amendment-card-body">
                                      <div className="amendment-fields-grid">
                                        <InputBox
                                          type="date"
                                          name="RequestDate"
                                          id="RequestDate_new"
                                          label="Request Date"
                                          fieldName="Request Date"
                                          value={
                                            currentRecord.RequestDate?.split(
                                              "T",
                                            )[0] || ""
                                          }
                                          readOnly
                                          {...inputProps}
                                        />
                                        <InputBox
                                          type="text"
                                          name="DrawingNo"
                                          id="DrawingNo_new"
                                          label="Drawing No. (Sales)*"
                                          fieldName="Drawing No. (Sales)"
                                          value={
                                            currentRecord.DrawingNo ||
                                            currentRecord.DwgNo ||
                                            ""
                                          }
                                          {...inputProps}
                                          error={recordErrors.DrawingNo}
                                          isAlphanumeric
                                          maxLength={20}
                                          required
                                        />
                                        <InputBox
                                          type="text"
                                          name="AdditionalDaysPOE"
                                          id="AdditionalDaysPOE_new"
                                          label="Additional Days (POE)"
                                          fieldName="Additional Days (POE)"
                                          value={
                                            currentRecord.AdditionalDaysPOE ||
                                            ""
                                          }
                                          readOnly
                                          {...inputProps}
                                          isNumeric
                                        />

                                        <InputBox
                                          type="text"
                                          name="ValueChange"
                                          id="ValueChange_new"
                                          label="Value Change (Price/Unit)"
                                          fieldName="Value Change (Price/Unit)"
                                          value={
                                            currentRecord.ValueChange || ""
                                          }
                                          readOnly
                                          {...inputProps}
                                          isNumeric
                                        />
                                        <InputBox
                                          type="text"
                                          name="WeightChange"
                                          id="WeightChange_new"
                                          label="Weight Change (Per/Order)"
                                          fieldName="Weight Change (Per/Order)"
                                          value={
                                            currentRecord.WeightChange || ""
                                          }
                                          readOnly
                                          {...inputProps}
                                          isNumeric
                                        />
                                        <InputBox
                                          type="text"
                                          name="AdditionalDaysMFG"
                                          id="AdditionalDaysMFG_new"
                                          label="Additional Days (MFG)"
                                          fieldName="Additional Days (MFG)"
                                          value={
                                            currentRecord.AdditionalDaysMFG ||
                                            ""
                                          }
                                          readOnly
                                          {...inputProps}
                                          isNumeric
                                        />
                                        <InputBox
                                          type="text"
                                          name="TechnicalChanges"
                                          id="TechnicalChanges_new"
                                          label="Technical Changes (Field/Unit)"
                                          fieldName="Technical Changes (Field/Unit)"
                                          value={
                                            currentRecord.TechnicalChanges || ""
                                          }
                                          readOnly
                                          {...inputProps}
                                        />
                                        <InputBox
                                          type="text"
                                          name="BodyCarryImpact"
                                          id="BodyCarryImpact_new"
                                          label="Body/Carry Impact (MFG)"
                                          fieldName="Body/Carry Impact (MFG)"
                                          value={
                                            currentRecord.BodyCarryImpact || ""
                                          }
                                          readOnly
                                          {...inputProps}
                                          isNumeric
                                        />
                                      </div>
                                      <InputBox
                                        type="text"
                                        as="textarea"
                                        style={{
                                          "max-width": "98%",
                                          "min-width": "50%",
                                          width: "-webkit-fill-available",
                                          "min-height": "30px",
                                        }}
                                        name="CustomerRequestDetails"
                                        id="CustomerRequestDetails_new"
                                        label="Customer Request Details (Sales)*"
                                        fieldName="Customer Request Details (Sales)"
                                        value={
                                          currentRecord.CustomerRequestDetails ||
                                          ""
                                        }
                                        {...inputProps}
                                        error={
                                          recordErrors.CustomerRequestDetails
                                        }
                                        isAlphanumeric
                                        maxLength={200}
                                        required
                                      />
                                      <div className="amendment-card-footer">
                                        <Button
                                          bsPrefix="reset-btn"
                                          className="reset-btn-primary"
                                          onClick={() =>
                                            handleReset(NEW_RECORD_KEY)
                                          }
                                        >
                                          Reset
                                        </Button>
                                        <Button
                                          variant="primary"
                                          className="btn-primary"
                                          onClick={handleSubmit}
                                          disabled={isSubmitDisabled()}
                                        >
                                          Submit
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                        })()}

                        {originalData.map((record, index) => {
                            const currentRecord = getEditedRecord(index);
                            const isExpanded = expandedIndex === index;
                            const inputProps = getCommonInputProps(index);
                            const recordErrors = errors[index] || {};
                            const isLast = index === originalData.length - 1;

                            return (
                                <div className="amendment-timeline-item" key={record.ID || index}>
                                    <div className="amendment-timeline-dot"></div>
                                    {!isLast && <div className="amendment-timeline-line"></div>}
                                    <div className="amendment-card">
                                        <div className="amendment-card-header" onClick={() => handleToggleExpand(index)}>
                                            <div className="amendment-header-left">
                                                <span className={`amendment-status-badge ${getStatusClass(currentRecord.AmendStatus)}`}>
                                                    {(() => {
                                                        const status = currentRecord.AmendStatus || "Pending for Approval";
                                                        const isSettled = ["approved", "rejected"].includes(status.toLowerCase());
                                                        if (!isSettled && currentRecord.PendingWithId) {
                                                            const name = salesUserMap[String(currentRecord.PendingWithId)] || currentRecord.PendingWithId;
                                                            return `${status} - Pending with - ${name}`;
                                                        }
                                                        return status;
                                                    })()}
                                                </span>
                                                <span className="amendment-date">
                                                    Requested date: {currentRecord.RequestDate ? formatDate(currentRecord.RequestDate) : "--"}
                                                </span>
                                            </div>
                                            <span className={`amendment-chevron ${isExpanded ? "expanded" : ""}`}>&#9662;</span>
                                        </div>

                                        {isExpanded && (
                                            <div className="amendment-card-body">
                                                <div className="amendment-fields-grid">
                                                    <InputBox type="date" name="RequestDate" id={`RequestDate_${index}`} label="Request Date" fieldName="Request Date" value={currentRecord.RequestDate ? new Date(currentRecord.RequestDate).toLocaleDateString('en-CA') : ""} readOnly {...inputProps} />
                                                    <InputBox type="text" name="DrawingNo" id={`DrawingNo_${index}`} label="Drawing No. (Sales)*" fieldName="Drawing No. (Sales)" value={currentRecord.DrawingNo || currentRecord.DwgNo || ""} readOnly {...inputProps} />
                                                     <InputBox type="text" name="AdditionalDaysPOE" id={`AdditionalDaysPOE_${index}`} label="Additional Days (POE)" fieldName="Additional Days (POE)" value={currentRecord.AdditionalDaysPOE || ""} readOnly {...inputProps} isNumeric />

                                                    <InputBox type="text" name="ValueChange" id={`ValueChange_${index}`} label="Value Change (Price/Unit)" fieldName="Value Change (Price/Unit)" value={currentRecord.ValueChange || ""} isNumeric readOnly {...inputProps} />
                                                    <InputBox type="text" name="WeightChange" id={`WeightChange_${index}`} label="Weight Change (Per/Order)" fieldName="Weight Change (Per/Order)" value={currentRecord.WeightChange || ""} isNumeric readOnly {...inputProps} />
                                                    <InputBox type="text" name="AdditionalDaysMFG" id={`AdditionalDaysMFG_${index}`} label="Additional Days (MFG)" fieldName="Additional Days (MFG)" value={currentRecord.AdditionalDaysMFG || ""} isNumeric readOnly {...inputProps} />
                                                    <InputBox type="text" name="TechnicalChanges" id={`TechnicalChanges_${index}`} label="Technical Changes (Field/Unit)" fieldName="Technical Changes (Field/Unit)" value={currentRecord.TechnicalChanges || ""} readOnly {...inputProps} />
                                                    <InputBox type="text" name="BodyCarryImpact" id={`BodyCarryImpact_${index}`} label="Body/Carry Impact (MFG)" fieldName="Body/Carry Impact (MFG)" value={currentRecord.BodyCarryImpact || ""} isNumeric readOnly {...inputProps} />
                                                </div>
                                                    <InputBox type="text" as="textarea" style={{"max-width": '98%',"min-width": '50%',"width": '-webkit-fill-available',"min-height": "30px"}} name="CustomerRequestDetails" id={`CustomerRequestDetails_${index}`} label="Customer Request Details (Sales)*" fieldName="Customer Request Details (Sales)" value={currentRecord.CustomerRequestDetails || ""} readOnly {...inputProps} />

                                                {(() => {
                                                    const files = amendmentFileNames[record.ID] || [];
                                                    // if (files.length === 0) return null;
                                                    return (
                                                        <>
                                                            <h6 className="section-subheading-plain amendment-subsection-title">Attachments</h6>
                                                            {files.length > 0 ? (
                                                                <Row className="add-edit-form drag_file low-margin">
                                                                    <Row>
                                                                        <Col className="file-attachment">
                                                                            {files.map((filename, i) => (
                                                                                <div key={i} className="inline-buttons uploaded-wrapper">
                                                                                    <span>
                                                                                        <Form.Text
                                                                                            className="fileupload-name view-file-name"
                                                                                            title={filename}
                                                                                        >
                                                                                            {filename}
                                                                                        </Form.Text>
                                                                                    </span>
                                                                                </div>
                                                                            ))}
                                                                        </Col>
                                                                    </Row>
                                                                </Row>
                                                            ) : (
                                                                <span className="no-files-message">No attachments found</span>
                                                            )
                                                            }
                                                        </>
                                                    );
                                                })()}

                                                <h6 className="section-subheading-plain amendment-subsection-title">Revised Timeline</h6>
                                                {isMobile ? (
                                                    <div className="isob-tab5-timeline-card-view">
                                                        {timelineDates.DesignStartDate && (
                                                            <div className="isob-tab5-mobile-card">
                                                                <div className="isob-tab5-mobile-card-title">Design</div>
                                                                <div className="isob-tab5-mobile-card-dates">
                                                                    <div className="isob-tab5-mobile-date-col">
                                                                        <span className="isob-tab5-mobile-date-label">Start date</span>
                                                                        <span className="isob-tab5-mobile-date-value">{formatDate(timelineDates.DesignStartDate) || "-"}</span>
                                                                    </div>
                                                                    <div className="isob-tab5-mobile-date-col">
                                                                        <span className="isob-tab5-mobile-date-label">Finish date</span>
                                                                        <span className="isob-tab5-mobile-date-value">{formatDate(timelineDates.DesignFinishDate) || "-"}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {timelineDates.SupplyStartDate && (
                                                            <div className="isob-tab5-mobile-card">
                                                                <div className="isob-tab5-mobile-card-title">Supply</div>
                                                                <div className="isob-tab5-mobile-card-dates">
                                                                    <div className="isob-tab5-mobile-date-col">
                                                                        <span className="isob-tab5-mobile-date-label">Start date</span>
                                                                        <span className="isob-tab5-mobile-date-value">{formatDate(timelineDates.SupplyStartDate) || "-"}</span>
                                                                    </div>
                                                                    <div className="isob-tab5-mobile-date-col">
                                                                        <span className="isob-tab5-mobile-date-label">Finish date</span>
                                                                        <span className="isob-tab5-mobile-date-value">{formatDate(timelineDates.SupplyFinishDate) || "-"}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {timelineDates.InstallationStartDate && (
                                                            <div className="isob-tab5-mobile-card">
                                                                <div className="isob-tab5-mobile-card-title">Installation</div>
                                                                <div className="isob-tab5-mobile-card-dates">
                                                                    <div className="isob-tab5-mobile-date-col">
                                                                        <span className="isob-tab5-mobile-date-label">Start date</span>
                                                                        <span className="isob-tab5-mobile-date-value">{formatDate(timelineDates.InstallationStartDate) || "-"}</span>
                                                                    </div>
                                                                    <div className="isob-tab5-mobile-date-col">
                                                                        <span className="isob-tab5-mobile-date-label">Finish date</span>
                                                                        <span className="isob-tab5-mobile-date-value">{formatDate(timelineDates.InstallationFinishDate) || "-"}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="amendment-timeline-table">
                                                        <div className="amendment-timeline-header">
                                                            <span></span>
                                                            <span>Start date</span>
                                                            <span>Finish date</span>
                                                        </div>
                                                        {timelineDates.DesignStartDate && (
                                                            <div className="amendment-timeline-row">
                                                                <span className="amendment-timeline-label">Design</span>
                                                                <span>{formatDate(timelineDates.DesignStartDate)}</span>
                                                                <span>{formatDate(timelineDates.DesignFinishDate)}</span>
                                                            </div>
                                                        )}
                                                        {timelineDates.SupplyStartDate && (
                                                            <div className="amendment-timeline-row">
                                                                <span className="amendment-timeline-label">Supply</span>
                                                                <span>{formatDate(timelineDates.SupplyStartDate)}</span>
                                                                <span>{formatDate(timelineDates.SupplyFinishDate)}</span>
                                                            </div>
                                                        )}
                                                        {timelineDates.InstallationStartDate && (
                                                            <div className="amendment-timeline-row">
                                                                <span className="amendment-timeline-label">Installation</span>
                                                                <span>{formatDate(timelineDates.InstallationStartDate)}</span>
                                                                <span>{formatDate(timelineDates.InstallationFinishDate)}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                <div className="amendment-approval-section">
                                                    <Button className="remarks-history-btn" onClick={() => {
                                                        setActiveApprovalComments(currentRecord.ApprovalComments || "");
                                                        setShowApprovalComments(true);
                                                    }}>
                                                        <img src={refreshTimeIcon} alt="" className="remarks-history-icon" /> Show Approval comments
                                                    </Button>
                                                </div>

                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <ApprovalCommentsModal
                show={showApprovalComments}
                onHide={() => setShowApprovalComments(false)}
                approvalComments={activeApprovalComments}
            />
        </>
    );
}
